import type { PixelOption } from "~/ui-rtk/api/campaignBlasterApi"
import type { Adset, CampaignData } from "../types"

interface FinalReviewProps {
  campaignTitle: string
  campaignData: CampaignData
  adsets: Adset[]
  onLaunch: () => void
  onBack: () => void
  pixels?: PixelOption[]
  selectedPixelId?: string | null
  onPixelChange?: (pixelId: string) => void
  recommendedPixelId?: string
  recommendedReason?: string
  pixelsLoading?: boolean
}

const FIELD_LABELS: Record<
  keyof Omit<
    CampaignData,
    | "ageMin"
    | "ageMax"
    | "ageRanges"
    | "platforms"
    | "budgetAmount"
    | "creativeOverrides"
  >,
  string
> = {
  campaignType: "Campaign Type",
  optimizationGoal: "Optimization Goal",
  attributionModel: "Attribution Model",
  attributionWindow: "Attribution Window",
  adsetStrategy: "Adset Strategy",
  targetingGeography: "Targeting Geography",
  targetingGender: "Targeting Gender",
  placements: "Placements",
  budgetOptimization: "Budget Optimization",
  biddingStrategy: "Bidding Strategy",
}

export default function FinalReview({
  campaignTitle,
  campaignData,
  adsets,
  onLaunch,
  onBack,
  pixels,
  selectedPixelId,
  onPixelChange,
  recommendedPixelId,
  recommendedReason,
  pixelsLoading,
}: FinalReviewProps) {
  const showPixelSelector =
    pixels && pixels.length > 0 && onPixelChange && selectedPixelId

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-basic-white mb-1">
          Final Review
        </h2>
        <p className="text-sm text-basic-light/60">
          Review your campaign before launching
        </p>
      </div>

      <div className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4">
        <p className="text-xs text-basic-light/50 mb-1">Campaign Title</p>
        <p className="text-sm text-basic-white font-mono break-all">
          {campaignTitle}
        </p>
      </div>

      <div className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-basic-light/70 mb-3">
          Campaign Settings
        </p>
        {(Object.keys(FIELD_LABELS) as Array<keyof typeof FIELD_LABELS>).map(
          key => (
            <div
              key={key}
              className="flex justify-between items-center py-1.5 border-b border-system-gray-700/30 last:border-0"
            >
              <span className="text-sm text-basic-light/50">
                {FIELD_LABELS[key]}
              </span>
              <span className="text-sm text-basic-white">
                {String(campaignData[key]) || "\u2014"}
              </span>
            </div>
          ),
        )}
        <div className="flex justify-between items-center py-1.5 border-b border-system-gray-700/30">
          <span className="text-sm text-basic-light/50">Platforms</span>
          <span className="text-sm text-basic-white">
            {campaignData.platforms.join(", ")}
          </span>
        </div>
        <div className="flex justify-between items-center py-1.5 border-b border-system-gray-700/30">
          <span className="text-sm text-basic-light/50">Daily Budget</span>
          <span className="text-sm text-basic-white">
            ${campaignData.budgetAmount.toFixed(2)} / day
          </span>
        </div>
        <div className="flex justify-between items-center py-1.5">
          <span className="text-sm text-basic-light/50">Age Range</span>
          <span className="text-sm text-basic-white">
            {campaignData.ageMin} - {campaignData.ageMax}
          </span>
        </div>
      </div>

      {pixelsLoading && (
        <div className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4">
          <p className="text-sm font-medium text-basic-light/70 mb-2">
            Meta Pixel
          </p>
          <div className="flex items-center gap-2">
            <span className="loading loading-spinner loading-sm text-primary" />
            <span className="text-sm text-basic-light/50">
              Finding the best pixel for your campaign...
            </span>
          </div>
        </div>
      )}

      {showPixelSelector && (
        <div className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4">
          <p className="text-sm font-medium text-basic-light/70 mb-2">
            Meta Pixel
          </p>
          <select
            className="select select-bordered w-full"
            value={selectedPixelId}
            onChange={e => onPixelChange(e.target.value)}
          >
            {pixels.map(pixel => (
              <option key={pixel.id} value={pixel.id}>
                {pixel.name}
                {pixel.id === recommendedPixelId ? " (recommended)" : ""}
              </option>
            ))}
          </select>
          {recommendedReason && (
            <p className="text-xs text-basic-light/50 mt-1.5">
              {recommendedReason}
            </p>
          )}
        </div>
      )}

      <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
        <p className="text-sm text-primary font-medium mb-1">
          Targeting Summary
        </p>
        <p className="text-sm text-basic-light/70">
          {campaignData.targetingGeography || "Any geography"},{" "}
          {campaignData.targetingGender === "All"
            ? "All genders"
            : campaignData.targetingGender || "Any gender"}
          , Ages {campaignData.ageMin}-{campaignData.ageMax},{" "}
          {campaignData.placements || "Any placement"}
          {" | "}
          {campaignData.platforms.join(", ")}
        </p>
      </div>

      <div className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4 space-y-2">
        <p className="text-sm font-medium text-basic-light/70">
          Adsets ({adsets.length})
        </p>
        {adsets.map((adset, i) => (
          <div
            key={adset.id}
            className="py-1.5 border-b border-system-gray-700/30 last:border-0"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs text-basic-light/40 w-6">{i + 1}.</span>
              <span className="text-sm text-basic-white">{adset.title}</span>
            </div>
            {adset.description && (
              <p className="text-xs text-basic-light/50 ml-8 mt-0.5">
                {adset.description}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
        <p className="text-sm text-warning font-medium mb-1">
          All assets will be created as PAUSED
        </p>
        <p className="text-xs text-basic-light/50">
          Campaign and adsets will be created in Meta Ads Manager with PAUSED
          status. Add ads via Ad Forge, then activate when ready.
        </p>
      </div>

      <div className="flex justify-between pt-4">
        <button className="btn btn-ghost" onClick={onBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <button
          className="btn btn-primary bg-gradient-to-r from-primary to-secondary border-0"
          onClick={onLaunch}
        >
          Launch Campaign
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
